import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useApi from "../hooks/useApi";
import { baseUrl } from "../config/apiConfig";
import ErrorPopUpComponent from "../components/ErrorPopUpComponent";
import { isPlatformAdmin } from "../util/accessControl";
import useAuth from "../hooks/useAuth";

const initForm = { name: "", email: "", password: "", role: 0, business_role: "" };

const Spinner = ({ dark = false }) => (
  <div
    className={`w-4 h-4 border-2 rounded-full animate-spin ${
      dark
        ? "border-indigo-300 border-t-indigo-600"
        : "border-white/30 border-t-white"
    }`}
  />
);

const RoleBadge = ({ role }) => {
  const map = {
    1: { label: "Admin", cls: "bg-violet-100 text-violet-700" },
    0: { label: "User",  cls: "bg-sky-100    text-sky-700"    },
  };
  const { label, cls } = map[role] ?? { label: `Role ${role}`, cls: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
};

/* ─── add / edit drawer ────────────────────────────────────────── */
const UserDrawer = ({ open, onClose, onSubmit, loading, editUser }) => {
  const isEdit = Boolean(editUser);
  const [form, setForm] = useState(initForm);

  useEffect(() => {
    if (open) {
      setForm(
        isEdit
          ? {
              name: editUser.name || "",
              email: editUser.email || "",
              password: "",
              role: editUser.role ?? 0,
              business_role: editUser.business_role || "",
            }
          : initForm
      );
    }
  }, [open, editUser]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-7 z-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? "Edit User" : "Add New User"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              id="user-name"
              type="text"
              value={form.name}
              onChange={set("name")}
              required
              placeholder="Alice Smith"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
            />
          </div>

          {/* email */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              id="user-email"
              type="email"
              value={form.email}
              onChange={set("email")}
              required={!isEdit}
              placeholder="alice@example.com"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
            />
          </div>

          {/* password */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Password {!isEdit && <span className="text-red-400">*</span>}
              {isEdit && <span className="text-gray-400">(leave blank to keep current)</span>}
            </label>
            <input
              id="user-password"
              type="password"
              value={form.password}
              onChange={set("password")}
              required={!isEdit}
              placeholder={isEdit ? "••••••••" : "Min 6 characters"}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
            />
          </div>

          {/* role */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <select
              id="user-role"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: Number(e.target.value) }))}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition bg-white"
            >
              <option value={0}>User</option>
              <option value={1}>Admin</option>
            </select>
          </div>

          {/* business role */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Business Role
            </label>
            <input
              id="user-business-role"
              type="text"
              value={form.business_role}
              onChange={set("business_role")}
              placeholder="e.g. LP, ALP, SM"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              id="user-submit-btn"
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2 transition"
            >
              {loading ? <Spinner /> : (isEdit ? "Save Changes" : "Add User")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── main page ──────────────────────────────────────────────────── */
const AdminOrgUsersPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { callApi } = useApi();

  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);   // null = add mode

  const [errorMessage, setErrorMessage] = useState("");
  const [isMessageShow, setIsMessageShow] = useState(false);

  const { user } = useAuth();
  const authorized = isPlatformAdmin(user);

  useEffect(() => {
    if (authorized) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, orgId]);

  /* ── api ── */
  const fetchUsers = async () => {
    setListLoading(true);
    try {
      const res = await callApi(`${baseUrl}/orgs/${orgId}/users`, "GET");
      if (res?.data) setUsers(res.data);
    } catch (err) {
      showError(err.message || "Failed to fetch users");
    } finally {
      setListLoading(false);
    }
  };

  const handleDrawerSubmit = async (form) => {
    setActionLoading(true);
    try {
      if (editUser) {
        // build only changed / non-empty fields
        const payload = {};
        if (form.name)          payload.name          = form.name;
        if (form.email)         payload.email         = form.email;
        if (form.password)      payload.password      = form.password;
        if (form.role !== null) payload.role          = form.role;
        if (form.business_role) payload.business_role = form.business_role;

        await callApi(
          `${baseUrl}/orgs/${orgId}/users/${editUser.id}`,
          "PUT",
          payload
        );
      } else {
        await callApi(`${baseUrl}/orgs/${orgId}/users`, "POST", {
          name:          form.name,
          email:         form.email,
          password:      form.password,
          role:          form.role,
          business_role: form.business_role || undefined,
        });
      }
      setDrawerOpen(false);
      setEditUser(null);
      await fetchUsers();
    } catch (err) {
      showError(err.message || "Operation failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openAdd  = () => { setEditUser(null);  setDrawerOpen(true); };
  const openEdit = (u) => { setEditUser(u);    setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); setEditUser(null); };

  const showError = (msg) => { setErrorMessage(msg); setIsMessageShow(true); };

  /* ── unauthorized ── */
  if (!authorized) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center p-10 bg-white shadow-xl rounded-2xl max-w-sm w-full border border-red-100">
          <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-500 text-sm">You don't have permission to manage users.</p>
        </div>
      </div>
    );
  }

  /* ── main ── */
  return (
    <>
      <UserDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSubmit={handleDrawerSubmit}
        loading={actionLoading}
        editUser={editUser}
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/40 pt-24 pb-10 px-4">
        <div className="max-w-5xl mx-auto space-y-8">

          {/* ── page header ── */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/orgs")}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-indigo-600 hover:border-indigo-300 transition shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="w-12 h-12 bg-indigo-600 text-white rounded-xl flex items-center justify-center shadow-md">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Org #{orgId} — Users</h1>
              <p className="text-sm text-gray-500 mt-0.5">Manage users in this organization</p>
            </div>
            <button
              id="add-user-btn"
              onClick={openAdd}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </button>
          </div>

          {/* ── user list ── */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-800">Users</h2>
              <span className="bg-indigo-50 text-indigo-600 text-xs font-semibold py-1 px-3 rounded-full">
                {users.length} total
              </span>
            </div>

            {listLoading ? (
              <div className="flex justify-center py-16">
                <Spinner dark />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
                <svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <p className="text-gray-500 font-medium">No users yet</p>
                <p className="text-gray-400 text-sm mt-1">Click "Add User" to get started.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    {/* card header */}
                    <div className="bg-gradient-to-r from-[#69afee] to-[#096dea] -m-5 mb-0 rounded-t-2xl px-4 pt-4 pb-3 flex items-start justify-between">
                      {/* avatar */}
                      <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center font-bold text-lg text-white">
                        {(user.name || "?").charAt(0).toUpperCase()}
                      </div>

                      {/* edit icon */}
                      <button
                        id={`edit-user-${user.id}`}
                        onClick={() => openEdit(user)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition"
                        title="Edit user"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536M9 11l6.364-6.364a2 2 0 012.828 2.828L11.828 13.828a4 4 0 01-1.414.94l-3.536.707.707-3.536A4 4 0 019 11z" />
                        </svg>
                      </button>
                    </div>

                    {/* name + email */}
                    <div>
                      <h3 className="text-base font-bold text-gray-800 truncate">{user.name}</h3>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{user.email}</p>
                    </div>

                    {/* badges */}
                    <div className="flex flex-wrap gap-1.5">
                      <RoleBadge role={user.role} />
                      {user.business_role && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
                          {user.business_role}
                        </span>
                      )}
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        {user.auth_source}
                      </span>
                    </div>

                    {/* footer */}
                    <div className="mt-auto pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-400">ID: #{user.id}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ErrorPopUpComponent
        isErrorShow={isMessageShow}
        errorMessage={errorMessage}
        redirect=""
      />
    </>
  );
};

export default AdminOrgUsersPage;
